import { DigitalAddressScreeningResult } from '@/types/digital-address-screening';
import { RequestData } from '@ant-design/pro-table';
import { normalizeEmail } from '@/utils/email';
import { request } from '@/utils/request';
import { trim } from 'lodash';

export interface DigitalDepositListItem {
  id: string;
  status: string;
  amount: string;
  date: Date;
  refId: string;
  network: string;
  fromAddress: string;
  fee: number;
  publicTransactionHash: string;
  receiptPhotoUrl?: string;
  wallet: {
    id: string;
    label: string;
    name: string;
    accountNumber: string;
  };
  user: {
    id: string;
    name: string;
    email: string;
    accountId: string;
  };
  vendor?: {
    id: number;
    name: string;
  };
  assetSymbol: string;
  description: string;
  returnAddress?: string;
  returnReason?: string;
  returnWithdrawalId?: string;
  returnWithdrawalRefId?: string;
  // Return metadata for list display of return status
  returnMetadata?: {
    pendingConfirmation?: boolean;
    initiatedAt?: string;
  };
  // Compliance case link
  caseId?: string;
  caseResolution?: string;
}

export interface ComplianceAlert {
  type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  message: string;
  details?: Record<string, any>;
  // Flattened fields for UI convenience
  alertLevel?: string;
  categoryId?: number;
  alertAmount?: number;
  exposureType?: string;
  externalId?: string;
}

export interface MatchedRule {
  action: string;
  category?: string[];
  categoryId?: number[];
  riskRating?: string;
}

export interface MatchedAlert {
  alertLevel: string;
  categoryId?: number;
  alertAmount?: number;
  exposureType?: string;
  externalId?: string;
  memo?: string;
  service?: string;
}

export interface ComplianceScreeningInfo {
  amlScreeningResult?: {
    decision: string;
    riskScore?: number;
    riskLevel?: string;
    provider?: string;
    completedAt?: string;
    alerts?: ComplianceAlert[];
    bypassReason?: string;
    bypassedAt?: string;
    bypassedBy?: string;
    prescreeningAction?: string;
    details?: Record<string, any>;
    // Extended fields from CHAINALYSIS_V2 format for direct UI access
    verdict?: string;
    category?: string;
    risk?: string;
    categoryId?: number;
    destAddress?: string;
    externalId?: string;
    matchedRule?: MatchedRule;
    matchedAlert?: MatchedAlert;
    labels?: string[];
    labelType?: string;
  };
  travelRuleResult?: {
    decision: string;
    required: boolean;
    vaspDetails?: {
      name: string;
      country: string;
      vaspId: string;
    };
    beneficiaryInfo?: Record<string, any>;
  };
  sanctionsScreeningResult?: {
    decision: string;
    matchedEntities?: Array<{
      name: string;
      type: string;
      listName: string;
      confidence: number;
    }>;
  };
  overallCompliance?: {
    status: 'APPROVED' | 'REJECTED' | 'PENDING' | 'REQUIRES_REVIEW';
    finalDecision: string;
    reviewRequired: boolean;
    processingTime?: number;
  };
  digitalAddressScreening?: DigitalAddressScreeningResult;
}

export interface TransferPeerInfo {
  status: string;
  attributes?: {
    address?: string;
    chainType?: string;
    walletOwnership?: 'SELF' | 'THIRD_PARTY';
    walletType?: 'SELF_HOSTED' | 'CUSTODIAL';
    entityType?: 'INDIVIDUAL' | 'CORPORATE';
    legalName?: string;
    country?: string;
    identificationNumber?: string;
    identificationType?: 'PASSPORT' | 'ID' | 'BR_CI';
    exchangePlatform?: string;
  };
}

/**
 * Aggregated KYT risk summary for an account/wallet
 */
export interface AccountRiskSummary {
  maxRiskScore: number | null;
  avgRiskScore: number | null;
  flaggedCount: number;
  totalScreenings: number;
  lastScreeningAt: string | null;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | null;
  /** Top risk categories seen for this wallet */
  topRiskCategories?: Array<{ category: string; count: number }>;
}

export interface DigitalDepositDetail extends DigitalDepositListItem {
  compliance?: ComplianceScreeningInfo;
  originatorTransferPeer?: TransferPeerInfo;
  returnMetadata?: ReturnMetadata;
  accountRisk?: AccountRiskSummary;
}

export enum DigitalDepositPendingStatus {
  PENDING = 'PENDING',
  SKIPPED = 'SKIPPED',
  PROCESSED = 'PROCESSED'
}

export enum DigitalDepositEventTypes {
  DigitalDepositConfirmed = 'digital_deposit.event.confirmed',
  DigitalDepositRejected = 'digital_deposit.event.rejected',
  DigitalDepositPendingInformation = 'digital_deposit.event.pending_information',
  DigitalDepositReturnInitiated = 'digital_deposit.event.return_initiated',
  DigitalDepositReturned = 'digital_deposit.event.returned',
  DigitalDepositVoided = 'digital_deposit.event.voided'
}

export interface DigitalDepositPendingListItem extends DigitalDepositListItem {
  transactionHash: string;
  destinationAddress: string;
  status: DigitalDepositPendingStatus;
  pendingReason: string;
  depositId: string;
}

export interface ReprocessDigitalDepositRequest {
  walletId: string;
  asset: string;
  chainType: string;
  operatorRemarks: string;
  remarkChannel: string;
  _yotpToken: string;
}

export interface ReprocessDigitalDepositResponse {
  success: boolean;
  message: string;
}

export interface BypassRejectedDepositRequest {
  bypassReason: string;
  operatorRemarks?: string;
  remarkChannel?: string;
  _yotpToken: string;
}

export interface BypassRejectedDepositResponse {
  success: boolean;
  message: string;
}

export interface ReturnToSenderRequest {
  returnReason: string;
  returnToAddress?: string;
  operatorRemarks?: string;
  remarkChannel?: string;
  _yotpToken: string;
}

export interface ReturnToSenderResponse {
  success: boolean;
  message: string;
  returnAddress: string;
  depositId: string;
}

// Two-step return flow types
export interface ReturnMetadata {
  pendingConfirmation?: boolean;
  clientMessage?: string;
  internalReason?: string;
  initiatedAt?: string;
  initiatedBy?: string;
  proposedReturnAddress?: string;
}

export interface InitiateReturnRequestRequest {
  internalReason: string;
  clientMessage: string;
  proposedReturnAddress: string;
  operatorRemarks?: string;
  remarkChannel?: string;
  _yotpToken: string;
}

export interface InitiateReturnRequestResponse {
  success: boolean;
  message: string;
  clientMessage: string;
  depositId: string;
}

export interface CancelReturnRequestResponse {
  success: boolean;
  message: string;
  depositId: string;
}

export interface UpdateOriginatorInfoRequest {
  intent: 'DEPOSIT_DIGITAL';
  walletOwnership: 'SELF' | 'THIRD_PARTY';
  walletType: 'SELF_HOSTED' | 'CUSTODIAL';
  entityType: 'INDIVIDUAL' | 'CORPORATE';
  legalName: string;
  country: string;
  identificationNumber: string;
  identificationType?: 'PASSPORT' | 'ID' | 'BR_CI';
  exchangePlatform?: string;
  _yotpToken: string;
  remarkChannel: string;
  remarks: string;
}

export interface UpdateOriginatorInfoResponse {
  success: boolean;
  message: string;
  transferPeerId: string;
}

export async function queryDigitalDeposit(params: any): Promise<RequestData<DigitalDepositListItem>> {
  if (params.date) {
    params.from = params.date[0];
    params.to = params.date[1];
    delete params.date;
  }
  if (params['user,email']) {
    params.email = normalizeEmail(params['user,email']);
    delete params['user,email'];
  }
  return await request<RequestData<DigitalDepositListItem>>('/digital-deposits', {
    params: {
      ...params,
      fromAddress: params.fromAddress ? trim(params.fromAddress) : undefined,
      publicTransactionHash: params.publicTransactionHash ? trim(params.publicTransactionHash) : undefined
    }
  });
}

export async function queryDigitalDepositDetail(params: { id: string }) {
  const data = await request<DigitalDepositDetail>(`/digital-deposits/${params.id}`);
  return {
    data
  };
}

/**
 * Fetch compliance information separately for async loading.
 * Allows the page to load even if compliance service is slow/unavailable.
 */
export async function queryDigitalDepositCompliance(params: { id: string }) {
  const data = await request<ComplianceScreeningInfo>(`/digital-deposits/${params.id}/compliance`);
  return {
    data
  };
}

export async function reprocessDigitalDeposit(id: string, params: ReprocessDigitalDepositRequest) {
  const data = await request<ReprocessDigitalDepositResponse>(`/digital-deposits/digital-deposits-pending/${id}/reprocess`, {
    method: 'POST',
    data: params
  });
  return {
    data
  };
}

export async function editDigitalDepositsDetail(params: { id: string; description: string }) {
  const postData = await request(`/digital-deposits/${params.id}/edit`, {
    method: 'POST',
    data: {
      ...params,
      method: 'post'
    }
  });
  return {
    postData
  };
}

export async function bypassRejectedDeposit(id: string, params: BypassRejectedDepositRequest) {
  const data = await request<BypassRejectedDepositResponse>(`/digital-deposits/${id}/bypass`, {
    method: 'POST',
    data: params
  });
  return {
    data
  };
}

export async function returnToSender(id: string, params: ReturnToSenderRequest) {
  const data = await request<ReturnToSenderResponse>(`/digital-deposits/${id}/return`, {
    method: 'POST',
    data: params
  });
  return {
    data
  };
}

export interface ResyncDepositResponse {
  success: boolean;
  message: string;
}

export async function resyncDeposit(id: string) {
  const data = await request<ResyncDepositResponse>(`/digital-deposits/${id}/resync`, {
    method: 'POST'
  });
  return {
    data
  };
}

// Batch Resync Types
export interface BatchResyncDepositsRequest {
  depositIds: string[];
}

export interface BatchResyncDepositResult {
  depositId: string;
  refId?: string;
  success: boolean;
  message: string;
}

export interface BatchResyncDepositsResponse {
  results: BatchResyncDepositResult[];
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
}

/**
 * Batch resync multiple digital deposits by resending Fireblocks webhooks
 */
export async function batchResyncDeposits(params: BatchResyncDepositsRequest) {
  const data = await request<BatchResyncDepositsResponse>('/digital-deposits/batch-resync', {
    method: 'POST',
    data: params
  });
  return {
    data
  };
}

/**
 * Step 1 of two-step return flow: Initiate return request
 */
export async function initiateReturnRequest(id: string, params: InitiateReturnRequestRequest) {
  const data = await request<InitiateReturnRequestResponse>(`/digital-deposits/${id}/initiate-return-request`, {
    method: 'POST',
    data: params
  });
  return {
    data
  };
}

/**
 * Cancel a pending return request
 */
export async function cancelReturnRequest(id: string) {
  const data = await request<CancelReturnRequestResponse>(`/digital-deposits/${id}/cancel-return-request`, {
    method: 'POST'
  });
  return {
    data
  };
}

export async function queryDigitalDepositPending(params: Record<string, any>): Promise<RequestData<DigitalDepositPendingListItem>> {
  if (params.date) {
    params.fromDate = params.date[0];
    params.toDate = params.date[1];
    delete params.date;
  }

  return await request<RequestData<DigitalDepositPendingListItem>>('/digital-deposits/digital-deposits-pending/list', {
    method: 'GET',
    params: params
  });
}

export async function updateOriginatorInfo(id: string, params: UpdateOriginatorInfoRequest) {
  return request<UpdateOriginatorInfoResponse>(`/digital-deposits/${id}/update-originator-info`, {
    method: 'POST',
    data: params
  });
}

// Edit-only originator info (no reprocessing)
export interface EditOriginatorInfoRequest {
  walletOwnership?: 'SELF' | 'THIRD_PARTY';
  walletType?: 'SELF_HOSTED' | 'CUSTODIAL';
  entityType?: 'INDIVIDUAL' | 'CORPORATE';
  legalName?: string;
  country?: string;
  identificationNumber?: string;
  identificationType?: 'PASSPORT' | 'ID' | 'BR_CI';
  exchangePlatform?: string;
  _yotpToken: string;
  remarkChannel?: string;
  remarks?: string;
}

export interface EditOriginatorInfoResponse {
  success: boolean;
  transferPeerId: string;
  digitalDepositId: string;
}

export async function editOriginatorInfo(id: string, params: EditOriginatorInfoRequest) {
  return request<EditOriginatorInfoResponse>(`/digital-deposits/${id}/originator-info`, {
    method: 'PATCH',
    data: params
  });
}

// KYT Screening Types
export type ScreeningVerdict = 'ACCEPT' | 'REVIEW' | 'INVESTIGATE' | 'REJECT' | 'UNKNOWN';
export type ScreeningReferenceType =
  | 'DIGITAL_DEPOSIT'
  | 'DIGITAL_WITHDRAWAL'
  | 'MANUAL_SCREENING'
  | 'TRANSACTION'
  | 'MONITORING';

export interface RiskCategory {
  category: string;
  subcategory?: string;
  confidence: number;
  description?: string;
}

export interface RiskExposure {
  type: string;
  value: number;
  confidence: number;
  timeframe: string;
  metadata?: {
    entity?: string;
    riskType?: string;
    hopCount?: number;
    volumeUsd?: number;
  };
}

export interface KytScreeningItem {
  id: string;
  address: string;
  assetSymbol: string;
  chainType: string;
  provider: string;
  verdict: ScreeningVerdict;
  riskScore: number;
  riskCategories: RiskCategory[];
  riskExposures: RiskExposure[];
  referenceId: string | null;
  referenceType: ScreeningReferenceType | null;
  createdAt: Date;
  // User info (from linked transaction)
  userId: string | null;
  accountId: string | null;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  // Transaction info
  amount: number | null;
}

export async function queryKytScreening(params: Record<string, any>): Promise<RequestData<KytScreeningItem>> {
  if (params.date) {
    params.fromDate = params.date[0];
    params.toDate = params.date[1];
    delete params.date;
  }

  return await request<RequestData<KytScreeningItem>>('/kyt-screening/list', {
    method: 'GET',
    params: params
  });
}

export interface DigitalDepositStats {
  pending: number;
  rejected: number;
  success: number;
  returned: number;
}

/**
 * Get digital deposit counts by status
 */
export async function getDigitalDepositStats(): Promise<DigitalDepositStats> {
  return request<DigitalDepositStats>('/digital-deposits/stats');
}
